/**
 * Story 8.12 — Onglet Références en lecture seule (modale profil membre)
 * Cartes cliquables style Instagram post.
 */
import React, { useState, useEffect } from 'react';
import type { ProfileData, Reference } from '../../../../../services/profileService';
import SectionPliable from '../../../../../composants/SectionPliable/SectionPliable';

interface ReadOnlyReferencesProps {
    data: ProfileData;
}

interface ProfileDataWithRefs extends ProfileData {
    references?: Reference[];
}

const ReadOnlyReferences: React.FC<ReadOnlyReferencesProps> = ({ data }) => {
    const references = (data as ProfileDataWithRefs).references ?? [];
    const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
    const apiUrl = process.env.REACT_APP_API_URL ?? '';

    const logoUrl = data.entite.logo_url ?? null;
    const entiteNom = data.entite.nom ?? '';
    const logoFallback = entiteNom[0]?.toUpperCase() ?? '?';

    const getPhotoSrc = (ref: Reference) =>
        ref.has_photo
            ? `${apiUrl}/annuaire/entites/${data.entite.id}/references/${ref.numero}/photo`
            : null;

    const selectedRef = selectedIdx !== null ? references[selectedIdx] : null;
    const hasPrev = selectedIdx !== null && selectedIdx > 0;
    const hasNext = selectedIdx !== null && selectedIdx < references.length - 1;

    const goTo = (idx: number, e: React.MouseEvent) => { e.stopPropagation(); setSelectedIdx(idx); };

    useEffect(() => {
        if (selectedIdx === null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft' && selectedIdx > 0) setSelectedIdx(selectedIdx - 1);
            else if (e.key === 'ArrowRight' && selectedIdx < references.length - 1) setSelectedIdx(selectedIdx + 1);
            else if (e.key === 'Escape') setSelectedIdx(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedIdx, references.length]);

    const formatBudget = (value: number) =>
        new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) + '€';

    return (
        <div className="readonly-tab readonly-references">
            {/* ── Overlay style Instagram ── */}
            {selectedRef && selectedIdx !== null && (() => {
                const photoSrc = getPhotoSrc(selectedRef);
                return (
                    <div className="readonly-ref-overlay" onClick={() => setSelectedIdx(null)}>
                        <button className="readonly-ref-overlay-close" onClick={() => setSelectedIdx(null)}>✕</button>

                        <div className="readonly-ref-insta-wrapper">
                            {/* Flèche précédente */}
                            {hasPrev && (
                                <button className="readonly-ref-nav readonly-ref-nav--prev" onClick={e => goTo(selectedIdx - 1, e)}>
                                    ‹
                                </button>
                            )}

                            <div className="readonly-ref-insta" onClick={e => e.stopPropagation()}>
                                {/* Côté gauche : image fixe avec bandeau noir */}
                                <div className="readonly-ref-insta-img-wrap">
                                    {photoSrc
                                        ? <img src={photoSrc} alt={selectedRef.nom_operation || 'Référence'} className="readonly-ref-insta-img" />
                                        : <div className="readonly-ref-insta-img--placeholder" />
                                    }
                                </div>
                                {/* Côté droit : contenu */}
                                <div className="readonly-ref-insta-content">
                                    <div className="readonly-ref-insta-header">
                                        <div className="readonly-ref-post-avatar">
                                            {logoUrl ? <img src={logoUrl} alt={entiteNom} /> : logoFallback}
                                        </div>
                                        <div className="readonly-ref-post-meta">
                                            <span className="readonly-ref-post-client">{entiteNom}</span>
                                            <span className="readonly-ref-insta-counter">{selectedIdx + 1} / {references.length}</span>
                                        </div>
                                    </div>
                                    {selectedRef.nom_operation && (
                                        <div className="readonly-ref-insta-title">{selectedRef.nom_operation}</div>
                                    )}
                                    <div className="readonly-ref-insta-fields">
                                        {selectedRef.client && (
                                            <div className="readonly-ref-insta-field">
                                                <span className="readonly-ref-insta-field-label">Client</span>
                                                <span className="readonly-ref-insta-field-value">{selectedRef.client}</span>
                                            </div>
                                        )}
                                        {selectedRef.annee_livraison && (
                                            <div className="readonly-ref-insta-field">
                                                <span className="readonly-ref-insta-field-label">Année</span>
                                                <span className="readonly-ref-insta-field-value">{selectedRef.annee_livraison}</span>
                                            </div>
                                        )}
                                        {selectedRef.budget && (
                                            <div className="readonly-ref-insta-field">
                                                <span className="readonly-ref-insta-field-label">Budget</span>
                                                <span className="readonly-ref-insta-field-value readonly-ref-post-budget">
                                                    {formatBudget(selectedRef.budget)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {selectedRef.descriptif && (
                                        <p className="readonly-ref-insta-desc">{selectedRef.descriptif}</p>
                                    )}
                                </div>
                            </div>

                            {/* Flèche suivante */}
                            {hasNext && (
                                <button className="readonly-ref-nav readonly-ref-nav--next" onClick={e => goTo(selectedIdx + 1, e)}>
                                    ›
                                </button>
                            )}
                        </div>{/* end readonly-ref-insta-wrapper */}
                    </div>
                );
            })()}

            <SectionPliable title="Références" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    {references.length === 0 ? (
                        <p className="readonly-empty">Aucune référence renseignée.</p>
                    ) : (
                        <div className="readonly-references-list">
                            {references.map((ref, idx) => {
                                const photoSrc = getPhotoSrc(ref);
                                return (
                                    <div
                                        key={ref.numero}
                                        className="readonly-ref-post"
                                        onClick={() => setSelectedIdx(idx)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={e => e.key === 'Enter' && setSelectedIdx(idx)}
                                    >
                                        {/* Cover photo */}
                                        {photoSrc ? (
                                            <div
                                                className="readonly-ref-post-cover"
                                                style={{ backgroundImage: `url(${photoSrc})` }}
                                            />
                                        ) : (
                                            <div className="readonly-ref-post-cover readonly-ref-post-cover--placeholder" />
                                        )}
                                        {/* Corps */}
                                        <div className="readonly-ref-post-body">
                                            <div className="readonly-ref-post-header">
                                                <div className="readonly-ref-post-avatar">
                                                    {logoUrl
                                                        ? <img src={logoUrl} alt={entiteNom} />
                                                        : logoFallback}
                                                </div>
                                                <div className="readonly-ref-post-meta">
                                                    <span className="readonly-ref-post-entite">{entiteNom}</span>
                                                    {ref.annee_livraison && (
                                                        <span className="readonly-ref-post-year">{ref.annee_livraison}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {ref.nom_operation && (
                                                <div className="readonly-ref-post-title">{ref.nom_operation}</div>
                                            )}
                                            {ref.client && (
                                                <div className="readonly-ref-post-client-line">
                                                    <span className="readonly-ref-post-client-label">Client :</span>
                                                    <span className="readonly-ref-post-client-value">{ref.client}</span>
                                                </div>
                                            )}
                                            {ref.descriptif && (
                                                <p className="readonly-ref-post-desc">{ref.descriptif}</p>
                                            )}
                                            {ref.budget && (
                                                <div className="readonly-ref-post-footer">
                                                    <span className="readonly-ref-post-budget">
                                                        {formatBudget(ref.budget)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SectionPliable>
        </div>
    );
};

export default ReadOnlyReferences;
